import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { fetchData } from "../utils";
import * as endpoints from '../settings';
import { schemas } from './schemas';
import sortBy from "lodash.sortby";
import { setNotification } from "./notificationsSlice";
import { getData, getDetails } from "./dataSlice";

export const getOrgAliases = createAsyncThunk(
    "getOrgAliases",
    async (arg, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetchData(endpoints.API_ORGS, {}, schemas.ALIASES);
            return sortBy(Object.values(response.entities.orgs), "title");
        } catch (e) {
            dispatch(setNotification(e));
        }
        return rejectWithValue([]);
    }
);

export const getJiraComponents = createAsyncThunk(
    "getJiraComponents",
    async (arg, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetchData(endpoints.API_SUPPORT_ISSUES_COMPONENTS, {}, schemas.COMPONENTS);
            return Object.values(response.entities.components);
        } catch (e) {
            dispatch(setNotification(e));
        }
        return rejectWithValue([]);
    }
);

export const getContracts = createAsyncThunk(
    "getContracts",
    async (arg, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetchData(endpoints.API_CONTRACTS, {}, schemas.CONTRACTS_LIST);
            return Object.values(response.entities.contracts);
        } catch (e) {
            dispatch(setNotification(e));
        }
        return rejectWithValue([]);
    }
);

export const getGrants = createAsyncThunk(
    "getGrants",
    async (arg, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetchData(endpoints.API_GRANTS, {}, schemas.GRANTS_LIST);
            return Object.values(response.entities.grants);
        } catch (e) {
            dispatch(setNotification(e));
        }
        return rejectWithValue([]);
    }
);

export const searchPackages = createAsyncThunk(
    "searchPackages",
    async (sysarch, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetchData(endpoints.API_PACKAGES_SEARCH, {
                method: "put",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: "",
                    sysarch: sysarch,
                    latestver: 0
                })
            }, schemas.SEARCH_PACKAGES);
            return Object.values(response.entities.packages);
        } catch (e) {
            dispatch(setNotification(e));
        }
        return rejectWithValue([]);
    }
);

const urls = {
    organisations: endpoints.API_ORG_DETAILS,
    users: endpoints.API_USER_DETAILS,
    issues: endpoints.API_SUPPORT_ISSUE,
    grants: endpoints.API_ISSUE_GRANT,
    contracts: endpoints.API_ORG_CONTRACT,
    packages: endpoints.API_PACKAGE,
    restrictions: endpoints.API_PACKAGE_RESTRICTIONS,
    prereq: endpoints.API_PACKAGE_PREREQ,
    contract: endpoints.API_CONTRACT
};

export const addItem = createAsyncThunk(
    "addItem",
    async({ entity, values }, { dispatch, getState }) => {
        try {
            let url = urls[entity],
            headers = entity === "issues" ? {} : { "Content-Type": "application/json" },
            method = entity === "prereq" || entity === "restrictions" ? "post" : "put";
            if (url) {
                if (Object.keys(getState().form.values).length && entity !== "grants" && entity !== "restrictions" && entity !== "prereq") url += "/update";
                const response = await fetchData(url, {
                    method: method,
                    headers: headers,
                    body: values
                }, {});
                let href = window.location.href.split("?"),
                search = href[1],
                manage = new URLSearchParams(search).get("manage"),
                parts = href[0].split("/"),
                page = parts[parts.indexOf("#") + 1],
                org, tab, selected;
                if (page === "organisations") {
                    org = parts[parts.indexOf(page) + 1];
                };
                // update data with new created item
                if (page && page !== "dashboard") {
                    if (entity === "organisations" || entity === "contracts") {
                        if (org) {
                            dispatch(getDetails({ page: page, org: org, selected: org }));
                        } else {
                            dispatch(getData({ tab: entity, manage: manage }));
                        };
                    } else {
                        tab = entity === "grants"
                            ? "users"
                            : entity === "restrictions" || entity === "prereq"
                                ? "packages"
                                : entity === "contract"
                                    ? "contracts"
                                    : entity;
                        let tabIndex = parts.indexOf(tab);
                        if (tabIndex > -1) {
                            // redirect to new package details page if package name has been changed
                            let { old_package_tag, new_package_tag } = JSON.parse(values);
                            if (typeof old_package_tag === "string" && typeof new_package_tag === "string" && old_package_tag !== new_package_tag && parts.includes(old_package_tag.toLowerCase())) {
                                parts.pop(old_package_tag);
                                parts.push(new_package_tag.toLowerCase());
                                window.location.href = parts.join("/");
                            } else {
                                selected = parts[tabIndex + 1];
                                if (selected) {
                                    dispatch(getDetails({ page: tab, selected: selected }));
                                } else if (org) {
                                    dispatch(getDetails({ page: page, org: org, tab: tab }));
                                } else {
                                    dispatch(getData({ tab: entity === "contract" ? "contracts" : entity, manage: manage }));
                                }
                            };
                        };
                    };
                };
                dispatch(setNotification(response.result));
            };
        } catch (e) {
            dispatch(setNotification(e));
        };
    }
);

const initialState = {
    visible: false,
    aliases: [],
    components: [],
    contracts: [],
    packages: [],
    grants: [],
    loading: false,
    hasChanges: false,
    values: {}
};

const form = createSlice({
    name: 'form',
    initialState,
    reducers: {
        showForm: (state, action) => {
            if (action.payload) {
                state.visible = action.payload;
                state.values = {};
                state.hasChanges = false;
            } else {
                return initialState;
            }
        },
        hasChanged: (state) => {
            state.hasChanges = true
        },
        editValues: (state, action) => {
            state.values = action.payload.values;
            state.visible = action.payload.entity;
            state.hasChanges = false;
        }
    },
    extraReducers: {
        [getOrgAliases.pending]: (state) => {
            state.loading = true;
        },
        [getOrgAliases.fulfilled]: (state, action) => {
            state.aliases = action.payload;
            state.loading = false;
        },
        [getOrgAliases.rejected]: (state) => {
            state.loading = false;
        },
        [getJiraComponents.pending]: (state) => {
            state.loading = true;
        },
        [getJiraComponents.fulfilled]: (state, action) => {
            state.components = action.payload;
            state.loading = false;
        },
        [getJiraComponents.rejected]: (state) => {
            state.loading = false;
        },
        [addItem.pending]: (state) => {
            state.visible = false;
            state.hasChanges = false;
        },
        [addItem.fulfilled]: (state) => {
            state.values = {};
        },
        [getContracts.fulfilled]: (state, action) => {
            state.contracts = action.payload;
        },
        [getGrants.fulfilled]: (state, action) => {
            state.grants = action.payload;
        },
        [searchPackages.pending]: (state, action) => {
            state.loading = true;
            state.packages = [];
        },
        [searchPackages.fulfilled]: (state, action) => {
            state.packages = action.payload;
            state.loading = false;
        },
        [searchPackages.rejected]: (state, action) => {
            state.loading = false;
        }
    }
});

export const { showForm, hasChanged, editValues } = form.actions;
export default form.reducer;